import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getBookingById, cancelBooking } from '../services/dataService';
import { getFormulaById } from '../config/formulas';
import '../styles/BookingStatus.css';

function BookingStatus() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelled, setCancelled] = useState(false);

  useEffect(() => {
    const loadBooking = async () => {
      try {
        const data = await getBookingById(bookingId);
        if (data) {
          setBooking(data);
        } else {
          setError("Réservation introuvable. Vérifiez votre lien.");
        }
      } catch (err) {
        setError("Impossible de charger la réservation.");
      } finally {
        setLoading(false);
      }
    };
    loadBooking();
  }, [bookingId]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await cancelBooking(bookingId);
      setCancelled(true);
      setShowCancelConfirm(false);
    } catch (err) {
      alert("Erreur lors de l'annulation. Contactez-nous au 02 44 84 99 96.");
    } finally {
      setCancelling(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Vérifier si l'annulation est encore possible (24h avant)
  const canCancel = () => {
    if (!booking || booking.status === 'cancelled') return false;
    const bookingDate = new Date(booking.date + 'T' + (booking.startTime || '00:00'));
    const now = new Date();
    const diffHours = (bookingDate - now) / (1000 * 60 * 60);
    return diffHours > 24;
  };

  if (loading) {
    return <div className="booking-status-container"><div className="bs-loading">Chargement de votre réservation...</div></div>;
  }

  if (error) {
    return (
      <div className="booking-status-container">
        <div className="bs-error">
          <div className="bs-error-icon">❌</div>
          <h2>Réservation introuvable</h2>
          <p>{error}</p>
          <p className="bs-contact">Besoin d'aide ? Appelez-nous au <strong>02 44 84 99 96</strong></p>
        </div>
      </div>
    );
  }

  if (cancelled) {
    return (
      <div className="booking-status-container">
        <div className="bs-cancelled-success">
          <div className="bs-icon">✅</div>
          <h2>Réservation annulée</h2>
          <p>Votre réservation du <strong>{formatDate(booking.date)}</strong> à <strong>{booking.startTime}</strong> a bien été annulée.</p>
          {booking.isPaid && (
            <p className="bs-refund-notice">
              💳 Si vous avez payé en ligne, le remboursement sera effectué sous 5 à 10 jours ouvrés.
              En cas de question, contactez-nous au <strong>02 44 84 99 96</strong>.
            </p>
          )}
          <a href="https://laserbay.net/reservation.html" className="bs-rebook-btn">
            Faire une nouvelle réservation
          </a>
        </div>
      </div>
    );
  }

  const formula = getFormulaById(booking.formulaId);
  const isCancelled = booking.status === 'cancelled';

  return (
    <div className="booking-status-container">
      <div className="bs-card">
        <div className={`bs-status-badge ${isCancelled ? 'cancelled' : booking.isPaid ? 'paid' : 'pending'}`}>
          {isCancelled ? '❌ Annulée' : booking.isPaid ? '✅ Confirmée & Payée' : '🕐 Confirmée — Paiement sur place'}
        </div>

        <h2 className="bs-title">Votre réservation LaserBay</h2>

        <div className="bs-details">
          <div className="bs-detail-row">
            <span className="bs-label">📅 Date</span>
            <span className="bs-value">{formatDate(booking.date)}</span>
          </div>
          <div className="bs-detail-row">
            <span className="bs-label">🕐 Heure</span>
            <span className="bs-value">{booking.startTime}</span>
          </div>
          <div className="bs-detail-row">
            <span className="bs-label">🎯 Formule</span>
            <span className="bs-value">{formula?.icon} {booking.formulaName}</span>
          </div>
          <div className="bs-detail-row">
            <span className="bs-label">👥 Joueurs</span>
            <span className="bs-value">{booking.nbParticipants} personne(s)</span>
          </div>
          <div className="bs-detail-row">
            <span className="bs-label">💰 Prix</span>
            <span className="bs-value bs-price">{booking.totalPrice}€</span>
          </div>
          <div className="bs-detail-row">
            <span className="bs-label">👤 Contact</span>
            <span className="bs-value">{booking.contactName}</span>
          </div>
          {booking.promoApplied && (
            <div className="bs-detail-row promo">
              <span className="bs-label">🎉 Promo</span>
              <span className="bs-value">{booking.promoApplied}</span>
            </div>
          )}
        </div>

        <div className="bs-reminder">
          <strong>📍 Rappel :</strong> Arrivez 15 minutes avant votre session pour l'équipement et le briefing.
          <br />LaserBay — 53 la Charrière du Commerce, 50230 Agon-Coutainville
        </div>

        {/* Annulation */}
        {!isCancelled && (
          <div className="bs-cancel-section">
            {canCancel() ? (
              <>
                {!showCancelConfirm ? (
                  <button 
                    className="bs-cancel-btn"
                    onClick={() => setShowCancelConfirm(true)}
                  >
                    Annuler cette réservation
                  </button>
                ) : (
                  <div className="bs-cancel-confirm">
                    <p>Êtes-vous sûr de vouloir annuler cette réservation ?</p>
                    <div className="bs-cancel-actions">
                      <button 
                        className="bs-confirm-cancel"
                        onClick={handleCancel}
                        disabled={cancelling}
                      >
                        {cancelling ? 'Annulation...' : 'Oui, annuler'}
                      </button>
                      <button 
                        className="bs-keep-btn"
                        onClick={() => setShowCancelConfirm(false)}
                      >
                        Non, garder ma réservation
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="bs-cancel-disabled">
                L'annulation en ligne n'est plus possible moins de 24h avant la session.
                Contactez-nous au <strong>02 44 84 99 96</strong>.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default BookingStatus;
